import { useEffect, useState } from 'react';
import { FiAlertCircle, FiAlertTriangle, FiArrowLeft, FiCheckCircle, FiCreditCard, FiDownload, FiEdit2, FiKey, FiPause, FiPlay, FiPlus, FiRefreshCw, FiTrash2, FiUploadCloud, FiXCircle } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { ImportResolutionWizard } from '../../components/imports/ImportResolutionWizard';
import {
    ImportReconciliationStep,
    isConciliado,
    type ReconciliationChoiceState,
    type ReconciliationItem,
    type ReconciliationOption,
    type ReconciliationSection,
} from '../../components/imports/ImportReconciliationStep';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { SearchableSelect, toast } from '../../components/ui';
import {
    adminApi,
    type AdminCustomerDataSummary,
    type AdminCustomerFilial,
    type AdminImportBatch,
    type AdminImportPreviewResponse,
    type AdminPlan,
    type AdminResetDataResult,
    type AdminCustomerDetail as DetailType,
} from '../../services/admin';
import type { ImportReconciliationMapping, ImportRowCorrection } from '../../types/imports';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';
import { limiteDeFiliais } from '../../utils/limites';

/** Palavra que o admin digita para liberar o reset de dados do cliente. */
const RESET_CONFIRM_WORD = 'EXCLUIR';

export function AdminCustomerDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<DetailType | null>(null);
    const [plans, setPlans] = useState<AdminPlan[]>([]);
    const [importBatches, setImportBatches] = useState<AdminImportBatch[]>([]);
    const [downloadingBatchId, setDownloadingBatchId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [planModalOpen, setPlanModalOpen] = useState(false);
    const [renewModalOpen, setRenewModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', email: '', whatsapp: '' });
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [resetSummary, setResetSummary] = useState<AdminCustomerDataSummary | null>(null);
    const [resetConfirmText, setResetConfirmText] = useState('');
    const [resetResult, setResetResult] = useState<AdminResetDataResult | null>(null);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [renewDate, setRenewDate] = useState('');
    const resetConfirmed = resetConfirmText.trim().toUpperCase() === RESET_CONFIRM_WORD;
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [customerFiliais, setCustomerFiliais] = useState<AdminCustomerFilial[]>([]);
    const [selectedFilialId, setSelectedFilialId] = useState('');
    const [importPreview, setImportPreview] = useState<AdminImportPreviewResponse | null>(null);
    const [importPreviewing, setImportPreviewing] = useState(false);
    const [importConfirming, setImportConfirming] = useState(false);
    const [importFixingRows, setImportFixingRows] = useState(false);
    const [importDownloadingErrors, setImportDownloadingErrors] = useState(false);
    const [keptDuplicateRowNumbers, setKeptDuplicateRowNumbers] = useState<Set<number>>(new Set());
    const [reviewedDuplicateRowNumbers, setReviewedDuplicateRowNumbers] = useState<Set<number>>(new Set());
    const [skipAllDuplicates, setSkipAllDuplicates] = useState(false);
    const [ignoredRowNumbers, setIgnoredRowNumbers] = useState<Set<number>>(new Set());
    const [subCategoryTypes, setSubCategoryTypes] = useState<Record<string, 'ENTRADA' | 'SAIDA'>>({});
    const [undoingBatchId, setUndoingBatchId] = useState<string | null>(null);
    const [importResumed, setImportResumed] = useState(false);
    const [importResuming, setImportResuming] = useState(false);
    // Etapa da conciliacao dentro do modal: 1 Grupos, 2 Categorias, 3 Revisao.
    const [importEtapa, setImportEtapa] = useState<1 | 2 | 3>(1);
    const [escolhasGrupos, setEscolhasGrupos] = useState<Record<string, ReconciliationChoiceState>>({});
    const [escolhasCategorias, setEscolhasCategorias] = useState<Record<string, ReconciliationChoiceState>>({});

    const [filialModalOpen, setFilialModalOpen] = useState(false);
    const [editingFilial, setEditingFilial] = useState<AdminCustomerFilial | null>(null);
    const [filialForm, setFilialForm] = useState({ name: '', cnpj: '' });
    const [filialActioningId, setFilialActioningId] = useState<string | null>(null);
    const [deletingFilial, setDeletingFilial] = useState<AdminCustomerFilial | null>(null);

    const load = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const [d, p, batches, filiais] = await Promise.all([
                adminApi.getCustomerDetail(id),
                adminApi.listPlans(),
                adminApi.getCustomerImportBatches(id),
                adminApi.getCustomerFiliais(id),
            ]);
            setDetail(d);
            setPlans(p);
            setImportBatches(batches);
            setCustomerFiliais(filiais);
            setSelectedPlanId(d.subscription?.planId ?? '');
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';
    const formatDateTime = (d?: string) => d ? new Date(d).toLocaleString('pt-BR') : '-';

    const importBatchStatusLabel: Record<string, string> = {
        PREVIEWED: 'Não confirmado', CONFIRMED: 'Confirmado', FAILED: 'Falhou', CANCELED: 'Desfeito',
    };

    const handleDownloadImportBatch = async (batchId: string) => {
        if (!id) return;
        setDownloadingBatchId(batchId);
        try {
            const data = await adminApi.getCustomerImportBatchRows(id, batchId);
            const motivoMap = new Map(data.discardedRows.map(row => [row.rowNumber, row.motivo]));
            const status = (row: typeof data.rows[number]) =>
                row.ignored ? (motivoMap.get(row.rowNumber) || 'Ignorada') : row.conflict ? 'Conflito' : row.duplicate ? 'Duplicata' : 'Ok';

            const lines: string[][] = [
                ['Batch ID', data.batchId],
                ['Arquivo', data.fileName],
                ['Total de linhas', String(data.summary.totalRows)],
                ['Linhas válidas', String(data.summary.totalValidRows)],
                ['Linhas ignoradas', String(data.summary.totalIgnoredRows)],
                ['Duplicatas', String(data.summary.totalDuplicates)],
                [],
                ['Linha', 'Data', 'Tipo', 'Categoria', 'Forma de pagamento', 'Valor', 'Status'],
                ...data.rows.map(row => [
                    String(row.rowNumber),
                    row.data || '-',
                    row.categoriaOriginal || '-',
                    row.subCategoriaOriginal || '-',
                    row.formaPagamentoOriginal || '-',
                    String(row.valorOriginal ?? '-'),
                    status(row),
                ]),
            ];

            const csv = lines
                .map(line => line.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
                .join('\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `importacao-${data.batchId}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            alert('Erro ao baixar a planilha do lote de importação.');
        } finally {
            setDownloadingBatchId(null);
        }
    };

    useEffect(() => {
        if (!importPreview) {
            setSubCategoryTypes({});
            return;
        }
        setSubCategoryTypes((current) => {
            const next = { ...current };
            for (const row of importPreview.indeterminate) {
                const key = row.normalizedKey || (row.subCategoriaOriginal || '').trim().toLowerCase();
                if (key && !next[key]) next[key] = 'SAIDA';
            }
            return next;
        });
    }, [importPreview]);

    const handleSetSubCategoryType = (key: string, tipo: 'ENTRADA' | 'SAIDA') => {
        setSubCategoryTypes((current) => ({ ...current, [key]: tipo }));
    };

    const reloadFiliais = async () => {
        if (!id) return;
        setCustomerFiliais(await adminApi.getCustomerFiliais(id));
    };

    const openNovaFilial = () => {
        setEditingFilial(null);
        setFilialForm({ name: '', cnpj: '' });
        setModalError(null);
        setFilialModalOpen(true);
    };

    const openEditarFilial = (filial: AdminCustomerFilial) => {
        setEditingFilial(filial);
        setFilialForm({ name: filial.name, cnpj: filial.cnpj ?? '' });
        setModalError(null);
        setFilialModalOpen(true);
    };

    const handleSalvarFilial = async () => {
        if (!id) return;
        if (!filialForm.name.trim()) { setModalError('Nome da filial é obrigatório'); return; }
        setSaving(true);
        setModalError(null);
        try {
            if (editingFilial) {
                await adminApi.updateCustomerFilial(id, editingFilial.id, { name: filialForm.name, cnpj: filialForm.cnpj });
                toast('Filial atualizada', 'success');
            } else {
                await adminApi.createCustomerFilial(id, { name: filialForm.name, cnpj: filialForm.cnpj || undefined });
                toast('Filial criada', 'success');
            }
            setFilialModalOpen(false);
            await reloadFiliais();
        } catch (e) {
            setModalError(e instanceof Error ? e.message : 'Erro ao salvar filial');
        } finally {
            setSaving(false);
        }
    };

    const handleDeletarFilial = async () => {
        if (!id || !deletingFilial) return;
        setFilialActioningId(deletingFilial.id);
        try {
            await adminApi.deleteCustomerFilial(id, deletingFilial.id);
            toast('Filial excluída', 'success');
            setDeletingFilial(null);
            await reloadFiliais();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Erro ao excluir filial', 'error');
        } finally {
            setFilialActioningId(null);
        }
    };

    // --- Conciliacao: mesmas etapas do fluxo do cliente, sobre o mesmo preview.
    const importReconciliation = importPreview?.reconciliation;
    const importOpcoes = importPreview?.opcoesDestino;

    const itensGruposImport: ReconciliationItem[] = (importReconciliation?.grupos ?? []).map((grupo) => ({
        key: grupo.key,
        planilhaNome: grupo.planilhaNome,
        tipo: grupo.tipo,
        sugestao: grupo.sugestao,
        detalhe: `${grupo.categoriasCount} subcategoria(s) · ${grupo.lancamentosCount} lançamento(s) identificado(s)`,
        filhos: (importReconciliation?.categorias ?? [])
            .filter((categoria) => categoria.grupoKey === grupo.key)
            .map((categoria) => categoria.planilhaNome),
    }));

    const itensCategoriasImport: ReconciliationItem[] = (importReconciliation?.categorias ?? []).map((categoria) => ({
        key: categoria.key,
        planilhaNome: categoria.planilhaNome,
        tipo: categoria.tipo,
        sugestao: categoria.sugestao,
        secaoKey: categoria.grupoKey,
        detalhe: `${categoria.lancamentosCount} lançamento(s) · ${categoria.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
    }));

    const destinoDoGrupoImport = (grupoKey: string): { id: string | null; nome: string } => {
        const grupo = importReconciliation?.grupos.find((item) => item.key === grupoKey);
        const escolha = escolhasGrupos[grupoKey];
        if (escolha?.destinoId) {
            const existente = importOpcoes?.grupos.find((opcao) => opcao.id === escolha.destinoId);
            if (existente) return { id: existente.id, nome: existente.nome };
        }
        const novo = escolha?.novoNome?.trim();
        if (novo) return { id: null, nome: novo };
        return { id: grupo?.sugestao.id ?? null, nome: grupo?.sugestao.nome ?? '—' };
    };

    const secoesCategoriasImport: ReconciliationSection[] = (importReconciliation?.grupos ?? []).map((grupo) => ({
        key: grupo.key,
        titulo: grupo.planilhaNome ?? 'Sem grupo na planilha',
        subtitulo: `Grupo no FD: ${destinoDoGrupoImport(grupo.key).nome}`,
    }));

    const opcoesParaGrupoImport = (item: ReconciliationItem): ReconciliationOption[] =>
        (importOpcoes?.grupos ?? []).filter((opcao) => opcao.tipo === item.tipo);

    const opcoesParaCategoriaImport = (item: ReconciliationItem): ReconciliationOption[] => {
        const todas = (importOpcoes?.categorias ?? []).filter((opcao) => opcao.tipo === item.tipo);
        if (!item.secaoKey) return todas;
        const grupo = destinoDoGrupoImport(item.secaoKey);
        if (!grupo.id) return [];
        return todas.filter((opcao) => opcao.grupoId === grupo.id);
    };

    const pendentesGruposImport = itensGruposImport.filter((item) => !isConciliado(item, escolhasGrupos[item.key])).length;
    const pendentesCategoriasImport = itensCategoriasImport.filter((item) => !isConciliado(item, escolhasCategorias[item.key])).length;
    const temConciliacaoImport = itensGruposImport.length > 0;

    const paraMappingImport = (escolhas: Record<string, ReconciliationChoiceState>) =>
        Object.entries(escolhas)
            .map(([key, escolha]) => ({ key, destinoId: escolha.destinoId, novoNome: escolha.novoNome?.trim() || undefined }))
            .filter((escolha) => escolha.destinoId || escolha.novoNome);

    const importMapping: ImportReconciliationMapping = {
        grupos: paraMappingImport(escolhasGrupos),
        categorias: paraMappingImport(escolhasCategorias),
    };

    const openImportModal = async () => {
        if (!id) return;
        setImportFile(null);
        setImportPreview(null);
        setSelectedFilialId('');
        setKeptDuplicateRowNumbers(new Set());
        setReviewedDuplicateRowNumbers(new Set());
        setSkipAllDuplicates(false);
        setIgnoredRowNumbers(new Set());
        setImportResumed(false);
        setModalError(null);
        setImportModalOpen(true);
        setImportResuming(true);
        try {
            const [filiais, pending] = await Promise.all([
                adminApi.getCustomerFiliais(id),
                adminApi.getPendingCustomerImportPreview(id),
            ]);
            setCustomerFiliais(filiais);
            if (filiais.length === 1) setSelectedFilialId(filiais[0].id);
            if (pending) {
                setImportPreview(pending);
                setImportResumed(true);
                setImportEtapa(pending.reconciliation && pending.reconciliation.grupos.length > 0 ? 1 : 3);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setImportResuming(false);
        }
    };

    const closeImportModal = () => {
        setImportModalOpen(false);
        setImportFile(null);
        setImportPreview(null);
        setModalError(null);
    };

    const handleStartNewImport = () => {
        setImportFile(null);
        setImportPreview(null);
        setImportResumed(false);
        setKeptDuplicateRowNumbers(new Set());
        setReviewedDuplicateRowNumbers(new Set());
        setModalError(null);
    };

    const handleGeneratePreview = async () => {
        if (!id || !importFile) {
            setModalError('Selecione um arquivo .xlsx ou .xlsm');
            return;
        }
        if (customerFiliais.length > 1 && !selectedFilialId) {
            setModalError('Selecione a filial de destino');
            return;
        }
        setModalError(null);
        setImportPreviewing(true);
        try {
            const response = await adminApi.previewCustomerImportBatch(id, importFile, selectedFilialId || undefined);
            setImportPreview(response);
            setEscolhasGrupos({});
            setEscolhasCategorias({});
            setImportEtapa(response.reconciliation && response.reconciliation.grupos.length > 0 ? 1 : 3);
        } catch (e: any) {
            setModalError(e.message ?? 'Erro ao importar a planilha');
        } finally {
            setImportPreviewing(false);
        }
    };

    const handleConfirmImport = async () => {
        if (!id || !importPreview) return;
        setImportConfirming(true);
        setModalError(null);
        try {
            // "Ignorar duplicatas potenciais" = nenhuma duplicata entra, então
            // manda a lista vazia. Sem ele, vale a decisão linha a linha.
            const response = await adminApi.confirmCustomerImportBatch(id, {
                batchPreviewId: importPreview.batchPreviewId,
                keepDuplicateRowNumbers: skipAllDuplicates ? [] : Array.from(keptDuplicateRowNumbers),
                resolutions: { subCategoryTypes },
                mapping: importMapping,
            });
            toast(`${response.totalsCriados} lançamento(s) importado(s) com sucesso`, 'success');
            closeImportModal();
            load();
        } catch (e: any) {
            setModalError(e.message ?? 'Erro ao confirmar importação');
        } finally {
            setImportConfirming(false);
        }
    };

    const handleToggleIgnoreRow = (rowNumber: number, ignore: boolean) => {
        setIgnoredRowNumbers((current) => {
            const next = new Set(current);
            if (ignore) next.add(rowNumber);
            else next.delete(rowNumber);
            return next;
        });
    };

    const handleToggleKeepDuplicate = (rowNumber: number, keep: boolean) => {
        setKeptDuplicateRowNumbers((current) => {
            const next = new Set(current);
            if (keep) next.add(rowNumber);
            else next.delete(rowNumber);
            return next;
        });
        setReviewedDuplicateRowNumbers((current) => {
            if (current.has(rowNumber)) return current;
            const next = new Set(current);
            next.add(rowNumber);
            return next;
        });
    };

    const handleRetryDiscardedImportRows = async (corrections: ImportRowCorrection[]) => {
        if (!id || !importPreview) return;
        setImportFixingRows(true);
        try {
            const response = await adminApi.fixCustomerImportBatchRows(id, importPreview.batchPreviewId, corrections);
            setImportPreview(response);
            setEscolhasGrupos({});
            setEscolhasCategorias({});
            const remaining = response.discardedRows.length + response.duplicateRows.length;
            toast(
                remaining === 0 ? 'Todas as linhas foram corrigidas' : `${remaining} linha(s) ainda precisam de atenção`,
                remaining === 0 ? 'success' : 'error',
            );
        } catch (e: any) {
            toast(e.message ?? 'Erro ao reprocessar linhas', 'error');
        } finally {
            setImportFixingRows(false);
        }
    };

    const handleDownloadDiscardedImportRows = async () => {
        if (!id || !importPreview) return;
        setImportDownloadingErrors(true);
        try {
            await adminApi.downloadCustomerImportDiscardedRows(id, importPreview.batchPreviewId);
        } catch (e: any) {
            toast(e.message ?? 'Erro ao baixar planilha de erros', 'error');
        } finally {
            setImportDownloadingErrors(false);
        }
    };

    const handleUndoImportBatch = async (batchId: string) => {
        if (!id || !confirm('Desfazer esta importação vai apagar os lançamentos criados por ela. Continuar?')) return;
        setUndoingBatchId(batchId);
        try {
            const response = await adminApi.deleteCustomerImportBatch(id, batchId);
            toast(`${response.deleted} lançamento(s) removido(s)`, 'success');
            load();
        } catch (e: any) {
            toast(e.message ?? 'Erro ao desfazer importação', 'error');
        } finally {
            setUndoingBatchId(null);
        }
    };

    const openEdit = () => {
        if (!detail) return;
        setEditForm({
            name: detail.name ?? '',
            email: detail.email ?? '',
            whatsapp: detail.whatsapp ?? '',
        });
        setModalError(null);
        setEditModalOpen(true);
    };

    const handleUpdateCustomer = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        setSaving(true);
        try {
            await adminApi.updateCustomer(id, {
                name: editForm.name.trim(),
                email: editForm.email.trim(),
                whatsapp: editForm.whatsapp.trim(),
            });
            setEditModalOpen(false);
            load();
        } catch (err: any) { setModalError(err.message); }
        finally { setSaving(false); }
    };

    const openSetPassword = () => {
        setPasswordForm({ newPassword: '', confirmPassword: '' });
        setModalError(null);
        setPasswordModalOpen(true);
    };

    const handleSetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        if (passwordForm.newPassword.length < 6) {
            setModalError('A senha precisa ter no mínimo 6 caracteres.');
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setModalError('As senhas não coincidem.');
            return;
        }
        setSaving(true);
        try {
            await adminApi.setCustomerPassword(id, passwordForm.newPassword);
            setPasswordModalOpen(false);
        } catch (err: any) { setModalError(err.message); }
        finally { setSaving(false); }
    };

    const openResetData = async () => {
        if (!id) return;
        setResetSummary(null);
        setResetResult(null);
        setResetConfirmText('');
        setModalError(null);
        setResetModalOpen(true);
        try {
            setResetSummary(await adminApi.getCustomerDataSummary(id));
        } catch (err: any) { setModalError(err.message); }
    };

    const handleResetData = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id || !detail) return;
        if (!resetConfirmed) {
            setModalError(`Digite ${RESET_CONFIRM_WORD} para confirmar.`);
            return;
        }
        setSaving(true);
        setModalError(null);
        try {
            // O backend cruza o e-mail com o id para garantir que é o cliente certo.
            setResetResult(await adminApi.resetCustomerData(id, detail.email));
            load();
        } catch (err: any) { setModalError(err.message); }
        finally { setSaving(false); }
    };

    const handleAssignPlan = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        setSaving(true);
        try {
            await adminApi.assignPlan(id, { planId: selectedPlanId });
            setPlanModalOpen(false);
            load();
        } catch (err: any) { setModalError(err.message); }
        finally { setSaving(false); }
    };

    const handleRenew = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        setSaving(true);
        try {
            await adminApi.renewSubscription(id, { expiresAt: renewDate });
            setRenewModalOpen(false);
            load();
        } catch (err: any) { setModalError(err.message); }
        finally { setSaving(false); }
    };

    const handleCancel = async () => {
        if (!id || !confirm('Cancelar assinatura?')) return;
        await adminApi.cancelSubscription(id);
        load();
    };

    const handleSuspend = async () => {
        if (!id || !confirm('Suspender assinatura?')) return;
        await adminApi.suspendSubscription(id);
        load();
    };

    const handleReactivate = async () => {
        if (!id) return;
        await adminApi.reactivateSubscription(id);
        load();
    };

    const handleStatusToggle = async () => {
        if (!id || !detail) return;
        const next = detail.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        const verb = next === 'INACTIVE' ? 'Inativar' : 'Ativar';
        if (!confirm(`${verb} ${detail.name}?`)) return;
        await adminApi.updateCustomerStatus(id, next);
        load();
    };

    const statusLabel: Record<string, string> = {
        ACTIVE: 'Ativa', TRIAL: 'Trial', PAST_DUE: 'Em atraso',
        CANCELED: 'Cancelada', SUSPENDED: 'Suspensa',
    };

    const allImportDuplicatesReviewed = skipAllDuplicates || (importPreview
        ? importPreview.duplicateRows.every((row) => reviewedDuplicateRowNumbers.has(row.rowNumber))
        : true);
    // Cada linha com problema precisa ter sido corrigida (some da lista) ou
    // marcada como ignorada.
    const allImportDiscardedResolved = !importPreview
        || importPreview.discardedRows.every((row) => ignoredRowNumbers.has(row.rowNumber));
    const canConfirmImport = !!importPreview
        && allImportDiscardedResolved
        && allImportDuplicatesReviewed;

    // Limite de negocios do plano do cliente ("Maximo de negocios" na tela de planos).
    const limiteFiliaisCliente = limiteDeFiliais(
        plans.find((plano) => plano.id === detail?.subscription?.planId)?.maxAccounts,
    );

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate('/admin/clientes')}>
                        <FiArrowLeft size={16} /> Voltar
                    </button>
                    <h2>{loading ? 'Carregando...' : detail?.name}</h2>
                    <p>{detail?.email}</p>
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingState}>Carregando...</div>
            ) : detail ? (
                <div className={styles.detailGrid}>
                    {/* Informações do usuário */}
                    <div className={styles.detailCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Informações do Usuário</h3>
                            <div className={styles.actionButtons} style={{ gap: '0.5rem' }}>
                                {detail.role !== 'ADMIN' && (
                                    <Button onClick={handleStatusToggle}>
                                        {detail.status === 'ACTIVE' ? <FiPause size={14} /> : <FiPlay size={14} />}
                                        {detail.status === 'ACTIVE' ? 'Inativar' : 'Ativar'}
                                    </Button>
                                )}
                                <Button onClick={openEdit}><FiEdit2 size={14} /> Editar</Button>
                                <Button onClick={openSetPassword}><FiKey size={14} /> Definir senha</Button>
                            </div>
                        </div>
                        <div className={styles.infoRows}>
                            <div className={styles.infoRow}><span>Nome</span><strong>{detail.name}</strong></div>
                            <div className={styles.infoRow}><span>Email</span><strong>{detail.email}</strong></div>
                            <div className={styles.infoRow}><span>WhatsApp</span><strong>{detail.whatsapp || '-'}</strong></div>
                            <div className={styles.infoRow}><span>Status</span>
                                <span className={`${styles.statusBadge} ${styles[detail.status.toLowerCase()]}`}>
                                    {detail.status === 'ACTIVE' ? 'Ativo' : detail.status === 'INACTIVE' ? 'Inativo' : 'Suspenso'}
                                </span>
                            </div>
                            <div className={styles.infoRow}><span>Cadastro</span><strong>{formatDate(detail.createdAt)}</strong></div>
                            <div className={styles.infoRow}><span>Último login</span><strong>{formatDateTime(detail.lastLoginAt)}</strong></div>
                        </div>
                    </div>

                    {/* Assinatura */}
                    <div className={styles.detailCard}>
                        <h3>Assinatura</h3>
                        {detail.subscription ? (
                            <>
                                <div className={styles.infoRows}>
                                    <div className={styles.infoRow}><span>Plano</span><strong>{detail.subscription.planName}</strong></div>
                                    <div className={styles.infoRow}><span>Status</span>
                                        <span className={`${styles.planBadge} ${detail.subscription.status === 'SUSPENDED' ? styles.planBadgeSuspended : detail.subscription.status === 'CANCELED' ? styles.planBadgeCanceled : ''}`}>{statusLabel[detail.subscription.status] ?? detail.subscription.status}</span>
                                    </div>
                                    <div className={styles.infoRow}><span>Início</span><strong>{formatDate(detail.subscription.startedAt)}</strong></div>
                                    <div className={styles.infoRow}><span>Vencimento</span><strong>{formatDate(detail.subscription.currentPeriodEnd)}</strong></div>
                                </div>
                                <div className={styles.actionButtons} style={{ marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <Button onClick={() => { setPlanModalOpen(true); setModalError(null); }}><FiCreditCard size={14} /> Alterar plano</Button>
                                    <Button onClick={() => { setRenewDate(''); setRenewModalOpen(true); setModalError(null); }}><FiRefreshCw size={14} /> Renovar</Button>
                                    {detail.subscription.status === 'SUSPENDED' ? (
                                        <Button onClick={handleReactivate}><FiCheckCircle size={14} /> Reativar</Button>
                                    ) : (
                                        <Button onClick={handleSuspend}><FiAlertCircle size={14} /> Suspender</Button>
                                    )}
                                    {detail.subscription.status !== 'CANCELED' && (
                                        <Button onClick={handleCancel}><FiXCircle size={14} /> Cancelar</Button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div>
                                <p style={{ color: 'var(--color-gray-500)', marginBottom: '1rem' }}>Sem assinatura ativa</p>
                                <Button onClick={() => { setPlanModalOpen(true); setModalError(null); }}><FiCreditCard size={14} /> Atribuir plano</Button>
                            </div>
                        )}
                    </div>

                    {/* Histórico de eventos */}
                    {(detail.subscriptionEvents?.length ?? 0) > 0 && (
                        <div className={`${styles.detailCard} ${styles.fullWidth}`}>
                            <h3>Histórico de Assinatura</h3>
                            <table className={styles.usersTable}>
                                <thead><tr><th>Evento</th><th>Plano anterior</th><th>Novo plano</th><th>Executado por</th><th>Data</th><th>Nota</th></tr></thead>
                                <tbody>
                                    {detail.subscriptionEvents!.map(ev => (
                                        <tr key={ev.id}>
                                            <td><span className={styles.planBadge}>{ev.type}</span></td>
                                            <td>{ev.fromPlanName ?? '-'}</td>
                                            <td>{ev.toPlanName ?? '-'}</td>
                                            <td>{ev.actorName ?? 'sistema'}</td>
                                            <td>{formatDateTime(ev.createdAt)}</td>
                                            <td>{ev.note ?? '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Filiais do cliente */}
                    <div className={`${styles.detailCard} ${styles.fullWidth}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Filiais</h3>
                            <Button
                                onClick={openNovaFilial}
                                disabled={customerFiliais.length >= limiteFiliaisCliente}
                                title={customerFiliais.length >= limiteFiliaisCliente ? `O plano do cliente permite até ${limiteFiliaisCliente} filial(is)` : undefined}
                            >
                                <FiPlus size={14} /> Nova filial
                            </Button>
                        </div>
                        {customerFiliais.length > 0 ? (
                            <table className={styles.usersTable}>
                                <thead>
                                    <tr><th>Nome</th><th>CNPJ</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {customerFiliais.map(filial => (
                                        <tr key={filial.id}>
                                            <td>{filial.name}</td>
                                            <td>{filial.cnpj || '-'}</td>
                                            <td>
                                                <div className={styles.actionButtons} style={{ gap: '0.5rem' }}>
                                                    <Button onClick={() => openEditarFilial(filial)}>
                                                        <FiEdit2 size={14} /> Editar
                                                    </Button>
                                                    <Button
                                                        className={styles.dangerBtn}
                                                        onClick={() => setDeletingFilial(filial)}
                                                        disabled={filialActioningId === filial.id || customerFiliais.length <= 1}
                                                    >
                                                        <FiTrash2 size={14} /> Excluir
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: 'var(--color-gray-500)', marginTop: '1rem' }}>
                                Este cliente ainda não possui filiais.
                            </p>
                        )}
                    </div>

                    {/* Importações de planilha */}
                    <div className={`${styles.detailCard} ${styles.fullWidth}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Importações de Planilha</h3>
                            <Button onClick={openImportModal}><FiUploadCloud size={14} /> Importar planilha</Button>
                        </div>
                        {importBatches.length > 0 ? (
                            <table className={styles.usersTable}>
                                <thead>
                                    <tr>
                                        <th>Arquivo</th>
                                        <th>Status</th>
                                        <th>Linhas lidas</th>
                                        <th>Importadas</th>
                                        <th>Ignoradas</th>
                                        <th>Data</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {importBatches.map(batch => (
                                        <tr key={batch.id}>
                                            <td>{batch.fileName}</td>
                                            <td>{importBatchStatusLabel[batch.status] ?? batch.status}</td>
                                            <td>{batch.totalLinhas}</td>
                                            <td>{batch.totalImportadas}</td>
                                            <td>{batch.totalIgnoradas}</td>
                                            <td>{formatDateTime(batch.createdAt)}</td>
                                            <td>
                                                <div className={styles.actionButtons} style={{ gap: '0.5rem' }}>
                                                    <Button
                                                        onClick={() => handleDownloadImportBatch(batch.id)}
                                                        disabled={downloadingBatchId === batch.id}
                                                    >
                                                        <FiDownload size={14} /> {downloadingBatchId === batch.id ? 'Baixando...' : 'Baixar CSV'}
                                                    </Button>
                                                    {batch.status === 'CONFIRMED' && (
                                                        <Button
                                                            onClick={() => handleUndoImportBatch(batch.id)}
                                                            disabled={undoingBatchId === batch.id}
                                                        >
                                                            <FiXCircle size={14} /> {undoingBatchId === batch.id ? 'Desfazendo...' : 'Desfazer'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: 'var(--color-gray-500)', marginTop: '1rem' }}>Nenhuma importação realizada ainda.</p>
                        )}
                    </div>

                    {/* Zona de perigo */}
                    <div className={`${styles.detailCard} ${styles.fullWidth} ${styles.dangerCard}`}>
                        <h3><FiAlertTriangle size={16} style={{ verticalAlign: '-2px', marginRight: '0.375rem' }} />Zona de Perigo</h3>
                        <p className={styles.dangerText}>
                            Zerar os dados financeiros apaga lançamentos, plano de contas, formas de pagamento
                            e o histórico de importações das filiais deste cliente, e recria o plano de contas
                            padrão — a conta volta ao estado de recém-criada. Login, senha, assinatura, plano e
                            filiais são mantidos. A ação é irreversível.
                        </p>
                        <Button className={styles.dangerBtn} onClick={openResetData}>
                            <FiTrash2 size={14} /> Zerar dados financeiros
                        </Button>
                    </div>

                    {/* Últimos acessos de features */}
                    {(detail.featureAccesses?.length ?? 0) > 0 && (
                        <div className={`${styles.detailCard} ${styles.fullWidth}`}>
                            <h3>Últimos Acessos a Features</h3>
                            <table className={styles.usersTable}>
                                <thead><tr><th>Feature</th><th>Último acesso</th></tr></thead>
                                <tbody>
                                    {detail.featureAccesses!.map(fa => (
                                        <tr key={fa.feature}>
                                            <td>{fa.feature}</td>
                                            <td>{formatDateTime(fa.lastAccess)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : (
                <div className={styles.loadingState}>Usuário não encontrado.</div>
            )}

            {/* Modal editar dados do usuário */}
            <Modal isOpen={editModalOpen} onClose={() => { setEditModalOpen(false); setModalError(null); }} title="Editar Usuário">
                <form onSubmit={handleUpdateCustomer}>
                    <div className={styles.formGroup}>
                        <Input label="Nome" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
                    </div>
                    <div className={styles.formGroup}>
                        <Input label="Email" type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} required />
                    </div>
                    <div className={styles.formGroup}>
                        <Input label="WhatsApp" value={editForm.whatsapp} onChange={e => setEditForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="5511999998888" />
                    </div>
                    {modalError && <p className={styles.formError}>{modalError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setEditModalOpen(false); setModalError(null); }}>Cancelar</Button>
                        <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
                    </div>
                </form>
            </Modal>

            {/* Modal nova/editar filial */}
            <Modal
                isOpen={filialModalOpen}
                onClose={() => { setFilialModalOpen(false); setModalError(null); }}
                title={editingFilial ? 'Editar Filial' : 'Nova Filial'}
            >
                <form onSubmit={e => { e.preventDefault(); handleSalvarFilial(); }}>
                    <div className={styles.formGroup}>
                        <Input
                            label="Nome da filial"
                            value={filialForm.name}
                            onChange={e => setFilialForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="Ex: Filial São Paulo"
                            required
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <Input
                            label="CNPJ (opcional)"
                            value={filialForm.cnpj}
                            onChange={e => setFilialForm(f => ({ ...f, cnpj: e.target.value }))}
                            placeholder="00.000.000/0000-00"
                        />
                    </div>
                    {!editingFilial && (
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                            A filial entra na mesma empresa das demais filiais deste cliente e já nasce com o
                            plano de contas padrão.
                        </p>
                    )}
                    {modalError && <p className={styles.formError}>{modalError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setFilialModalOpen(false); setModalError(null); }}>Cancelar</Button>
                        <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
                    </div>
                </form>
            </Modal>

            {/* Modal confirmar exclusão de filial */}
            <Modal isOpen={!!deletingFilial} onClose={() => setDeletingFilial(null)} title="Excluir Filial">
                <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
                    Excluir a filial <strong>{deletingFilial?.name}</strong> deste cliente?
                </p>
                <p className={styles.dangerText}>
                    Só é possível excluir filial sem nenhum lançamento. O plano de contas e as formas de
                    pagamento dela são apagados junto. A ação é irreversível.
                </p>
                <div className={styles.modalFooter}>
                    <Button type="button" onClick={() => setDeletingFilial(null)}>Cancelar</Button>
                    <Button
                        className={styles.dangerBtn}
                        onClick={handleDeletarFilial}
                        disabled={filialActioningId === deletingFilial?.id}
                    >
                        {filialActioningId === deletingFilial?.id ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </div>
            </Modal>

            {/* Modal definir senha */}
            <Modal isOpen={passwordModalOpen} onClose={() => { setPasswordModalOpen(false); setModalError(null); }} title="Definir Senha do Cliente">
                <form onSubmit={handleSetPassword}>
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                        Define a senha diretamente, sem depender de e-mail. O cliente pode logar com ela imediatamente.
                    </p>
                    <div className={styles.formGroup}>
                        <Input
                            label="Nova senha"
                            type="password"
                            value={passwordForm.newPassword}
                            onChange={e => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                            placeholder="Mínimo 6 caracteres"
                            required
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <Input
                            label="Confirmar nova senha"
                            type="password"
                            value={passwordForm.confirmPassword}
                            onChange={e => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                            placeholder="Repita a senha"
                            required
                        />
                    </div>
                    {modalError && <p className={styles.formError}>{modalError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setPasswordModalOpen(false); setModalError(null); }}>Cancelar</Button>
                        <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Definir senha'}</Button>
                    </div>
                </form>
            </Modal>

            {/* Modal zerar dados financeiros */}
            <Modal isOpen={resetModalOpen} onClose={() => { setResetModalOpen(false); setModalError(null); }} title="Zerar Dados Financeiros">
                {resetResult ? (
                    <div>
                        <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
                            Pronto. {resetResult.total} registro(s) apagado(s) e plano de contas padrão
                            recriado em {resetResult.filiaisReprovisionadas} filial(is).
                        </p>
                        <table className={styles.usersTable}>
                            <thead><tr><th>Tabela</th><th>Removidos</th></tr></thead>
                            <tbody>
                                {(resetSummary?.itens ?? []).map(item => (
                                    <tr key={item.key}>
                                        <td>{item.label}</td>
                                        <td>{resetResult.deleted[item.key] ?? 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className={styles.modalFooter}>
                            <Button type="button" onClick={() => setResetModalOpen(false)}>Fechar</Button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleResetData}>
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            Isto apaga em definitivo os dados abaixo. Conta, senha, assinatura, plano e filiais
                            permanecem. Não há como desfazer.
                        </p>

                        {!resetSummary ? (
                            <p className={styles.loadingState}>Carregando dados do cliente...</p>
                        ) : (
                            <>
                                <table className={styles.usersTable} style={{ marginBottom: '1rem' }}>
                                    <thead><tr><th>Tabela</th><th>Registros</th></tr></thead>
                                    <tbody>
                                        {resetSummary.itens.map(item => (
                                            <tr key={item.key}>
                                                <td>{item.label}</td>
                                                <td>{item.count}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td><strong>Total</strong></td>
                                            <td><strong>{resetSummary.total}</strong></td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Filiais afetadas</label>
                                    <ul style={{ fontSize: '0.8125rem', paddingLeft: '1.125rem', margin: 0 }}>
                                        {resetSummary.filiais.length === 0 && <li>Nenhuma filial vinculada.</li>}
                                        {resetSummary.filiais.map(f => (
                                            <li key={f.id}>
                                                {f.name}
                                                {f.outrosUsuarios > 0 && (
                                                    <strong style={{ color: 'var(--color-danger-600, #dc2626)' }}>
                                                        {' '}— compartilhada com outros {f.outrosUsuarios} usuário(s), que também perderão esses dados
                                                    </strong>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                        )}

                        <div className={styles.formGroup}>
                            <Input
                                label={`Digite ${RESET_CONFIRM_WORD} para liberar o botão`}
                                value={resetConfirmText}
                                onChange={e => setResetConfirmText(e.target.value)}
                                placeholder={RESET_CONFIRM_WORD}
                                autoComplete="off"
                                required
                            />
                        </div>

                        {modalError && <p className={styles.formError}>{modalError}</p>}
                        <div className={styles.modalFooter}>
                            <Button type="button" onClick={() => { setResetModalOpen(false); setModalError(null); }}>Cancelar</Button>
                            <Button
                                type="submit"
                                className={styles.dangerBtn}
                                disabled={saving || !resetSummary || !resetConfirmed}
                            >
                                {saving ? 'Zerando...' : 'OK'}
                            </Button>
                        </div>
                    </form>
                )}
            </Modal>

            {/* Modal alterar plano */}
            <Modal isOpen={planModalOpen} onClose={() => { setPlanModalOpen(false); setModalError(null); }} title="Alterar Plano">
                <form onSubmit={handleAssignPlan}>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Plano</label>
                        <SearchableSelect
                            className={styles.formSelect}
                            value={selectedPlanId}
                            onChange={setSelectedPlanId}
                            emptyLabel="Sem plano"
                            options={plans.map(p => ({ value: p.id, label: `${p.name} — R$ ${Number(p.price).toFixed(2)}/${p.interval === 'yearly' ? 'ano' : 'mês'}` }))}
                            fullWidth
                            dropdownVariant="inline"
                        />
                    </div>
                    {modalError && <p className={styles.formError}>{modalError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setPlanModalOpen(false); setModalError(null); }}>Cancelar</Button>
                        <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Confirmar'}</Button>
                    </div>
                </form>
            </Modal>

            {/* Modal renovar */}
            <Modal isOpen={renewModalOpen} onClose={() => { setRenewModalOpen(false); setModalError(null); }} title="Renovar Assinatura">
                <form onSubmit={handleRenew}>
                    <div className={styles.formGroup}>
                        <Input label="Nova data de vencimento" type="date" value={renewDate} onChange={e => setRenewDate(e.target.value)} />
                    </div>
                    {modalError && <p className={styles.formError}>{modalError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setRenewModalOpen(false); setModalError(null); }}>Cancelar</Button>
                        <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Renovar'}</Button>
                    </div>
                </form>
            </Modal>

            {/* Modal importar planilha */}
            <Modal isOpen={importModalOpen} onClose={closeImportModal} title="Importar Planilha do Cliente" size="xlarge">
                {/*
                  * Fora das etapas de propósito: a retomada abre na conciliação,
                  * e o botão de recomeçar só existia na revisão — só aparecia
                  * depois de percorrer o wizard inteiro.
                  */}
                {importResumed && importPreview && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: '10px', background: 'var(--color-primary-50, #f0fdfa)', border: '1px solid var(--color-primary, #0f766e)', fontSize: '0.875rem' }}>
                        <span>Retomando a revisão de <strong>{importPreview.fileName || 'uma importação pendente'}</strong> — o progresso não foi perdido.</span>
                        <Button type="button" variant="outline" onClick={handleStartNewImport}>Começar nova importação</Button>
                    </div>
                )}

                {importResuming ? (
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>Verificando importação pendente...</p>
                ) : !importPreview ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.875rem 1rem', marginBottom: '1rem', border: '1px solid #86efac', borderRadius: '10px', background: '#f0fdf4', color: '#166534' }}>
                            <FiCheckCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
                            <div>
                                <strong style={{ display: 'block', fontSize: '0.875rem' }}>Importe apenas a "Planilha de Controle Financeiro - LTQ Educação"</strong>
                                <span style={{ display: 'block', marginTop: '2px', fontSize: '0.8125rem', color: '#15803d' }}>
                                    É o único modelo que o Financeiro Diário sabe ler. Outro arquivo, mesmo em Excel, tende a vir incompleto ou nem ser aceito.
                                </span>
                            </div>
                        </div>
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            Aceita .xlsx, .xlsm (aba "Base de Dados") e .csv até 10 MB. O sistema tenta resolver tipo, categoria e forma de pagamento com base na planilha e no plano de contas do cliente.
                        </p>
                        {customerFiliais.length > 1 && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Filial de destino</label>
                                <SearchableSelect
                                    className={styles.formSelect}
                                    value={selectedFilialId}
                                    onChange={setSelectedFilialId}
                                    emptyLabel="Selecione a filial"
                                    options={customerFiliais.map(f => ({ value: f.id, label: f.name }))}
                                    fullWidth
                                    dropdownVariant="inline"
                                />
                            </div>
                        )}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Arquivo</label>
                            <input
                                type="file"
                                accept=".xlsx,.xlsm,.csv"
                                onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                            />
                            {importFile && <p style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>{importFile.name}</p>}
                        </div>
                        {modalError && <p className={styles.formError}>{modalError}</p>}
                        <div className={styles.modalFooter}>
                            <Button type="button" onClick={closeImportModal}>Cancelar</Button>
                            <Button type="button" onClick={handleGeneratePreview} disabled={importPreviewing}>
                                {importPreviewing ? 'Importando...' : 'Importar'}
                            </Button>
                        </div>
                    </div>
                ) : importEtapa === 1 ? (
                    <div>
                        <ImportReconciliationStep
                            modo="grupos"
                            itens={itensGruposImport}
                            opcoesPara={opcoesParaGrupoImport}
                            escolhas={escolhasGrupos}
                            onChange={(key, escolha) => setEscolhasGrupos((atual) => ({ ...atual, [key]: escolha }))}
                        />
                        <div className={styles.modalFooter}>
                            <Button type="button" onClick={() => setImportPreview(null)}>Voltar</Button>
                            <Button
                                type="button"
                                onClick={() => setImportEtapa(2)}
                                disabled={pendentesGruposImport > 0}
                            >
                                Continuar
                            </Button>
                        </div>
                    </div>
                ) : importEtapa === 2 ? (
                    <div>
                        <ImportReconciliationStep
                            modo="categorias"
                            itens={itensCategoriasImport}
                            opcoesPara={opcoesParaCategoriaImport}
                            escolhas={escolhasCategorias}
                            onChange={(key, escolha) => setEscolhasCategorias((atual) => ({ ...atual, [key]: escolha }))}
                            secoes={secoesCategoriasImport}
                        />
                        <div className={styles.modalFooter}>
                            <Button type="button" onClick={() => setImportEtapa(1)}>Voltar</Button>
                            <Button
                                type="button"
                                onClick={() => setImportEtapa(3)}
                                disabled={pendentesCategoriasImport > 0}
                            >
                                Continuar
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                            <div><strong>{importPreview.summary.totalValidRows}</strong> linhas válidas</div>
                            <div><strong>{importPreview.summary.totalIgnoredRows}</strong> Linhas com problemas</div>
                            <div><strong>{importPreview.summary.totalDuplicates}</strong> Duplicatas Potenciais</div>
                        </div>

                        {importPreview.entitiesToCreate.categorias.length + importPreview.entitiesToCreate.subCategorias.length + importPreview.entitiesToCreate.formasPagamento.length > 0 && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Entidades que serão criadas</label>
                                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                    {importPreview.entitiesToCreate.categorias.length} categoria(s), {importPreview.entitiesToCreate.subCategorias.length} subcategoria(s), {importPreview.entitiesToCreate.formasPagamento.length} forma(s) de pagamento
                                </p>
                            </div>
                        )}

                        {(importPreview.indeterminate.length > 0 || importPreview.discardedRows.length > 0 || importPreview.duplicateRows.length > 0) && (
                            <div className={styles.formGroup}>
                                <ImportResolutionWizard
                                    batchId={importPreview.batchPreviewId}
                                    opcoesDestino={importPreview.opcoesDestino}
                                    indeterminate={importPreview.indeterminate}
                                    subCategoryTypes={subCategoryTypes}
                                    onSetSubCategoryType={handleSetSubCategoryType}
                                    discardedRows={importPreview.discardedRows}
                                    onRetryDiscarded={handleRetryDiscardedImportRows}
                                    onDownloadDiscarded={handleDownloadDiscardedImportRows}
                                    retryingDiscarded={importFixingRows}
                                    downloadingDiscarded={importDownloadingErrors}
                                    duplicateRows={importPreview.duplicateRows}
                                    keptDuplicateRowNumbers={keptDuplicateRowNumbers}
                                    reviewedDuplicateRowNumbers={reviewedDuplicateRowNumbers}
                                    onToggleKeepDuplicate={handleToggleKeepDuplicate}
                                    skipAllDuplicates={skipAllDuplicates}
                                    onSkipAllDuplicatesChange={setSkipAllDuplicates}
                                    ignoredRowNumbers={ignoredRowNumbers}
                                    onToggleIgnoreRow={handleToggleIgnoreRow}
                                />
                            </div>
                        )}

                        {modalError && <p className={styles.formError}>{modalError}</p>}

                        <div className={styles.modalFooter}>
                            <Button type="button" onClick={() => (temConciliacaoImport ? setImportEtapa(2) : setImportPreview(null))}>Voltar</Button>
                            <Button type="button" onClick={handleConfirmImport} disabled={importConfirming || !canConfirmImport}>
                                {importConfirming ? 'Confirmando...' : 'Confirmar importação'}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </AdminLayout>
    );
}
